import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Shield, FileText, Briefcase, CheckCircle, ChevronLeft, ChevronRight, GraduationCap } from 'lucide-react';
import { Card, Button } from '../components';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

type SlideKey = 'privacy' | 'reporting' | 'dba';

interface ElearningStatus {
  module_version: string;
  slides: Record<SlideKey, string | null>;
  completed: boolean;
  completed_at: string | null;
}

const SLIDES: { key: SlideKey; icon: React.ElementType; paragraphs: number }[] = [
  { key: 'privacy', icon: Shield, paragraphs: 3 },
  { key: 'reporting', icon: FileText, paragraphs: 3 },
  { key: 'dba', icon: Briefcase, paragraphs: 4 },
];

export const ComplianceElearningPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('compliance');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [checked, setChecked] = useState<Record<SlideKey, boolean>>({
    privacy: false,
    reporting: false,
    dba: false,
  });

  const { data: statusData, isLoading } = useQuery<{ success: boolean; data: ElearningStatus }>({
    queryKey: ['compliance-elearning'],
    queryFn: async () => {
      const response = await api.get('/professional/compliance-elearning');
      return response.data;
    },
  });

  const status = statusData?.data;

  // Server-side al bevestigde slides ook lokaal aanvinken
  useEffect(() => {
    if (status) {
      setChecked(prev => ({
        privacy: prev.privacy || !!status.slides.privacy,
        reporting: prev.reporting || !!status.slides.reporting,
        dba: prev.dba || !!status.slides.dba,
      }));
    }
  }, [status]);

  const ackMutation = useMutation({
    mutationFn: async (slide: SlideKey) => {
      const response = await api.post('/professional/compliance-elearning/ack', {
        slide,
        acknowledged: 'true',
      });
      return response.data as { success: boolean; data: ElearningStatus };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['compliance-elearning'], data);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('error.ackFailed'));
    },
  });

  const slide = SLIDES[currentSlide];
  const isLastSlide = currentSlide === SLIDES.length - 1;
  const slideAcked = checked[slide.key];

  const handleCheckbox = (value: boolean) => {
    setChecked(prev => ({ ...prev, [slide.key]: value }));
    // Bevestiging direct persisteren voor audit-trail (alleen bij aanvinken)
    if (value && !status?.slides[slide.key]) {
      ackMutation.mutate(slide.key);
    }
  };

  const handleNext = async () => {
    if (!slideAcked) {
      toast.error(t('error.checkboxRequired'));
      return;
    }
    // Zorg dat de bevestiging op de server staat voordat we verder gaan
    if (!status?.slides[slide.key] && !ackMutation.isPending) {
      try {
        await ackMutation.mutateAsync(slide.key);
      } catch {
        return;
      }
    }
    if (isLastSlide) {
      toast.success(t('completedToast'));
      navigate('/professional/contract');
    } else {
      setCurrentSlide(s => s + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // Al volledig afgerond → toon bevestiging + doorsturen
  if (status?.completed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('alreadyCompleted.title')}</h2>
            <p className="text-gray-600 mb-6">{t('alreadyCompleted.desc')}</p>
            <Button onClick={() => navigate('/professional/contract')}>
              {t('alreadyCompleted.toContract')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const SlideIcon = slide.icon;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <img
            src="/belagom-text.png"
            alt="BeLagom"
            className="h-12 mx-auto cursor-pointer mb-6"
            onClick={() => navigate('/')}
          />
          <div className="flex items-center justify-center gap-2 mb-2">
            <GraduationCap className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          </div>
          <p className="text-gray-600">{t('subtitle')}</p>
        </div>

        {/* Voortgangsindicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          {SLIDES.map((s, i) => (
            <React.Fragment key={s.key}>
              <div
                className={`flex items-center justify-center h-9 w-9 rounded-full text-sm font-semibold border-2 ${
                  checked[s.key]
                    ? 'bg-green-500 border-green-500 text-white'
                    : i === currentSlide
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {checked[s.key] ? <CheckCircle className="h-5 w-5" /> : i + 1}
              </div>
              {i < SLIDES.length - 1 && <div className="h-0.5 w-10 bg-gray-300" />}
            </React.Fragment>
          ))}
        </div>

        <Card>
          <div className="flex items-start gap-4 mb-6">
            <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <SlideIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-1">
                {t('slideLabel', { current: currentSlide + 1, total: SLIDES.length })}
              </p>
              <h2 className="text-xl font-bold text-gray-900">{t(`slides.${slide.key}.title`)}</h2>
            </div>
          </div>

          <div className="space-y-4 text-gray-700 leading-relaxed mb-6">
            {Array.from({ length: slide.paragraphs }, (_, i) => (
              <p key={i}>{t(`slides.${slide.key}.p${i + 1}`)}</p>
            ))}
          </div>

          {/* Harde actie: verplichte verklaring */}
          <div className={`rounded-lg border-2 p-4 ${slideAcked ? 'border-green-400 bg-green-50' : 'border-blue-200 bg-blue-50'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={slideAcked}
                onChange={(e) => handleCheckbox(e.target.checked)}
                disabled={!!status?.slides[slide.key]}
                className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={`text-sm font-medium ${slideAcked ? 'text-green-800' : 'text-gray-800'}`}>
                {t(`slides.${slide.key}.declaration`)}
              </span>
            </label>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
              disabled={currentSlide === 0}
              className="flex items-center"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('previous')}
            </Button>
            <Button
              onClick={handleNext}
              disabled={!slideAcked || ackMutation.isPending}
              className="flex items-center"
            >
              {isLastSlide ? t('finish') : t('next')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
